import { FC, useEffect, useState } from 'react';
import { useGrowthBook } from '@growthbook/growthbook-react';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import classNames from 'classnames';

import { defaultFlowModel } from '@/features/default-flow/models/default-flow.model';
import { useAppSelector, useLoader, useTracking } from '@/hooks';
import { FlowModel } from '@/models/flow.model';

import CompanyRequests from '../../requests/company.requests';
import { type CustomCompanySettings, OnboardingStepsMap, StepInfo } from '../../types';
import { RenderIf } from '../RenderIf';
import StepHeader from '../StepHeader/StepHeader';

import styles from './Step.module.scss';

type StepProps = Omit<StepInfo<OnboardingStepsMap>, 'Component'> & {
  flow?: FlowModel;
}

export const Step: FC<StepProps> = (
  {
    id,
    stepName,
    title,
    desc,
    Info,
    children,
    flow = defaultFlowModel,
  },
) => {
  const { company } = useAppSelector(state => state.auth);
  const { trackingMode, hasCompletedOnboarding, currentStep } = useAppSelector(state => state.onboarding);
  const [latestStep, setLatestStep] = useState(id);

  const { showLoader } = useLoader();
  const { trackStepChangePage, trackStepChangeEvent } = useTracking(flow);
  const growthBook = useGrowthBook();

  useEffect(() => {
    // Update the onboarding_step attribute in GrowthBook on every step change
    if (growthBook) growthBook.setAttributes({ ...growthBook.getAttributes(), onboarding_step: id });

    if (currentStep.isDefault) {
      setLatestStep(id);
      return;
    }

    if (
      latestStep !== id  &&
      stepName !== 'Tracking Type Screen'
    ) {
      trackStepChangeEvent(trackingMode);
      trackStepChangePage(stepName);

      const custom: CustomCompanySettings = {
        onboardingStep: id,
        showDemoData: true,
      };

      CompanyRequests.putCompanySettings(company?.id || '', {
        custom,
      });

      setLatestStep(id);
    }
  }, [
    id,
    stepName,
    company,
    currentStep,
    latestStep,
    trackingMode,
    trackStepChangeEvent,
    trackStepChangePage,
    growthBook,
  ]);

  useEffect(() => {
    if (hasCompletedOnboarding) showLoader();
  }, [hasCompletedOnboarding, showLoader]);

  return (
    <>
      <RenderIf condition={ stepName !== 'Tracking Type Screen' && Boolean(title) }>
        <StepHeader
          title={ title }
          desc={ desc }
          className={ styles.Step__TextWrap }
        />
      </RenderIf>


      { children }

      <RenderIf
        condition={ !!Info &&
          stepName !== 'Tracking Type Screen' &&
          stepName !== 'About You Screen' }>
        <p className={ classNames(styles.Step__Info, 'color-secondary-brown') }>
          <InfoIcon fontSize="small" />
          <span className="ft-size-14">{ Info }</span>
        </p>
      </RenderIf>
    </>
  );
};
